import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'

/**
 * The document header's styles, shared by the desktop layout, the mobile layout, and the small pieces
 * they have in common (HeaderLineLink, the contact menu's divider). One sheet rather than one per file
 * because the two layouts share the card shell, the accent tick, the link-hover pill, and the
 * `clickableEmpty → $emptyState` rule — a JSS rule reference only resolves within its own sheet.
 */
export const useQuoteDocumentHeaderStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    const border = `1px solid ${neutral.P90}`
    // Shared hover treatment for the inline links in the header (contact name, phone/email/address
    // deep-links, date chip): a subtle rounded P96 pill, with negative margins so it doesn't shift
    // layout. Kept in one place so every link hovers identically.
    const linkHoverPill = {
      cursor: 'pointer',
      borderRadius: theme.borderRadius.sm,
      margin: theme.spacing(-0.25, -0.5),
      padding: theme.spacing(0.25, 0.5),
      transition: 'background-color 150ms ease',
      '&:hover': {
        backgroundColor: neutral.P96
      }
    }
    return {
      root: {
        border,
        borderRadius: 8,
        backgroundColor: neutral.White,
        overflow: 'hidden',
        marginBottom: theme.spacing(2)
      },

      // When embedded at the top of a shared card (with the item section), the header drops its own
      // border/rounding/gap so it reads as the top region of that one card rather than a separate one.
      flush: {
        border: 'none',
        borderRadius: 0,
        marginBottom: 0
      },

      // --- company section ---
      companySection: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        // Never wrap: the doc label + ID stay pinned to the right at any width, and long company info
        // wraps within its own column instead (see companyBlock's zero flex-basis).
        flexWrap: 'nowrap',
        gap: theme.spacing(3),
        padding: theme.spacing(4),
        [theme.breakpoints.down('xs')]: {
          padding: theme.spacing(2.5)
        }
      },

      // Whole-card edit affordance: pointer + subtle hover wash so it reads as clickable, matching the
      // EditContact/EditDeal detail cards. Applied to each editable region (columns + company block).
      clickable: {
        cursor: 'pointer',
        transition: 'background-color 150ms ease',
        '&:hover': {
          backgroundColor: neutral.P98
        }
      },
      // An *empty* clickable column hovers differently: instead of washing the whole column grey, the
      // dashed placeholder inside it lights up blue so it reads as the button it is (the column has no
      // other content to speak of, so the wash just looked like a dead grey block).
      clickableEmpty: {
        cursor: 'pointer',
        '&:hover $emptyState, &:focus-visible $emptyState': {
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main
        }
      },
      // An individual value (phone / email / address) that deep-links (tel:/mailto:/maps) on click.
      // Stops the click from also opening the card's edit dialog; hover recolors like DetailDocList.
      lineLink: {
        ...linkHoverPill
      },
      companyBlock: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(3),
        // Fill the row so the meta line wraps within this column instead of running into the doc
        // block (Estimate / ID) on the right; minWidth:0 lets it actually shrink and wrap.
        // A *zero* basis is what keeps the doc block on the right: with `auto`, this column's flex
        // base is the meta row's max-content width, which overflows the row and pushes the doc block
        // to the next line before the meta row ever wraps.
        flex: '1 1 0',
        minWidth: 0
      },
      logo: {
        maxWidth: 120,
        maxHeight: 120,
        objectFit: 'contain',
        flexShrink: 0
      },
      companyInfo: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        paddingTop: theme.spacing(0.5)
      },
      heading: {
        fontSize: theme.typography.pxToRem(22),
        fontWeight: 500,
        lineHeight: 1.2,
        color: neutral.P30
      },
      metaRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(2.5),
        marginTop: theme.spacing(1.25),
        color: neutral.P50,
        fontSize: theme.typography.pxToRem(14)
      },
      // Free-text "Additional Company Info" from settings (companyAdditional / the quote-type company
      // override). Sits under the meta row with the same accent tick the column labels use, so it reads
      // as a deliberate note rather than another meta item. Newlines in the setting are preserved.
      additionalRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(0.75),
        marginTop: theme.spacing(1.25)
      },
      // Same 3x12 accent tick the column labels use. It keeps its fixed height (rather than stretching
      // with the text) so a multi-line note still reads as one chip beside its first line — hence the
      // small top offset that centres it against that line.
      additionalTick: {
        marginTop: 4,
        marginLeft: '7.5px',
        marginRight: '9px'
      },
      additionalText: {
        color: neutral.P50,
        whiteSpace: 'pre-line',
        minWidth: 0
      },
      metaItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        minWidth: 0
      },
      // Font Awesome fills its em box, where the Material icon this replaced drew its glyph inset in a
      // 24-unit viewBox (~80% ink). Sizes are scaled by 0.8 so the icons read at their old size (ENG-1366).
      metaIcon: {
        fontSize: 14,
        color: neutral.P50
      },
      // Issued/Expires renders as a plain inline meta item (icon + text) matching the address/phone
      // items — no chip/pill background. It's still the one clickable affordance in the header (opens
      // the same date/expiry editor the classic estimate header used, QuoteDateCard).
      datePill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        color: neutral.P50,
        fontSize: theme.typography.pxToRem(14),
        whiteSpace: 'nowrap'
      },
      datePillClickable: {
        ...linkHoverPill
      },
      // Padded panel that hosts the Issued/Expiry editor (two collapsible calendar cards).
      datePopover: {
        padding: theme.spacing(0.75),
        minWidth: 320
      },
      docBlock: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        flexShrink: 0,
        // Keeps "Work Order" / "Estimate" and the ID on single lines at the right edge.
        whiteSpace: 'nowrap',
        marginLeft: 'auto',
        paddingTop: theme.spacing(0.5)
      },
      docId: {
        marginTop: theme.spacing(1.25),
        color: neutral.P60,
        fontSize: theme.typography.pxToRem(14)
      },

      // --- columns section ---
      // Flex-wrap (not a fixed grid) reflows the three contact columns from 3-up → 2-up → stacked
      // based on the card's actual width (so it adapts to mobile *and* to the sidebar squeezing the
      // estimate column), rather than a fixed viewport breakpoint. `flex-grow` makes each column fill
      // its row, so a lone column that wraps onto its own row stretches full-width instead of leaving
      // a grey half-cell.
      //
      // Dividers are painted box-shadows on each column (see `column`), clipped here. They used to be
      // a 1px flex gap over a P90 background, but a gap is the *absence* of paint: `flex-grow` gives
      // the columns fractional widths, and when the browser snaps their backgrounds to device pixels
      // the two rects can round toward each other and close the gap, so the divider silently vanishes
      // at some container widths / display scalings. A painted shadow always gets its pixel.
      columns: {
        display: 'flex',
        flexWrap: 'wrap',
        borderTop: border,
        // Clips the leading divider of every row (see `column`) — this is what keeps the shadow
        // approach correct at any column count and wrap point.
        overflow: 'hidden'
      },
      // Compact columns (mock #23): tighter padding, gaps, and type so the row reads dense.
      column: {
        position: 'relative',
        flex: '1 1 200px',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        padding: theme.spacing(2, 3),
        minWidth: 0,
        backgroundColor: neutral.White,
        // Left + top dividers, drawn 1px outside the box so each column separates itself from the one
        // before it (and, once wrapped, from the row above). Outer shadows paint outside the border
        // box, so the first column of *every* row has its left shadow land outside `columns` and get
        // clipped — which a `:not(:first-child)` border can't do once the columns wrap onto a second
        // row. Columns are `position: relative`, so each paints over its predecessor in DOM order.
        boxShadow: `-1px 0 0 ${neutral.P90}, 0 -1px 0 ${neutral.P90}`,
        [theme.breakpoints.down('xs')]: {
          padding: theme.spacing(1.5)
        }
      },
      labelRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75)
      },
      tick: {
        width: 3,
        height: 12,
        borderRadius: 2,
        backgroundColor: theme.paintscout.palette.brand.Neutral.P50,
        flexShrink: 0
      },
      // An empty column's tick drops to a muted neutral, so the accent reads as "this has content" and
      // the empty column sits back visually next to its dashed placeholder.
      tickEmpty: {
        backgroundColor: neutral.P85
      },
      columnLabel: {
        color: neutral.P50,
        fontSize: theme.typography.pxToRem(12)
      },
      nameGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: 0
      },
      primaryText: {
        fontWeight: 600,
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P40
      },
      // Clickable contact name → opens the contact profile. Inline pill with a P96 hover wash; stops
      // propagation so it doesn't also open the column's edit menu.
      contactNameLink: {
        display: 'inline-block',
        ...linkHoverPill
      },
      mutedText: {
        color: neutral.P60,
        fontSize: theme.typography.pxToRem(12)
      },
      lineItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        color: neutral.P50,
        fontSize: theme.typography.pxToRem(13),
        // Lets the value shrink: a flex item's min-width is otherwise its min-content width, so a
        // long email widened the column instead of ellipsizing (ENG-1635).
        minWidth: 0,
        // Inherited by the value, and inert wherever that value is `nowrap`. Covers the wrapping
        // case, where an unbreakable token overflows whatever the flex sizing says.
        overflowWrap: 'anywhere'
      },
      /**
       * Cuts a one-line value off with an ellipsis instead of letting it overflow its column, which
       * is only 200px at its flex basis (ENG-1635).
       *
       * Opt-in per call site, never on a shared text style: the values in these columns are a mix.
       * Emails, phones and names are single-line and should ellipsize; an address is meant to wrap,
       * and its first line borrows the same `primaryText` style a name uses.
       */
      truncate: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0
      },
      // Multi-line values (e.g. a wrapped address) keep the icon pinned to the top line rather than
      // vertically centered against the whole block.
      lineItemTop: {
        alignItems: 'flex-start'
      },
      lineIcon: {
        fontSize: 15,
        color: neutral.P50,
        flexShrink: 0,
        marginTop: 1
      },
      // Reserves the avatar's footprint so a long estimator name ellipsizes before reaching it
      // rather than running underneath (ENG-1635). The avatar is absolute and takes no width, so
      // the inset goes on the two elements it overlaps — padding the column would also indent the
      // detail lines, which it never reaches. Reserved rather than hidden below some column width:
      // hiding needs the column measured, and with the name ellipsizing it would only buy 40px.
      columnWithAvatar: {
        '& $labelRow, & $nameGroup': {
          paddingRight: 32 + theme.spacing(1)
        }
      },
      // Estimator avatar pinned top-right of the Sales Rep column (mock #23).
      salesRepAvatar: {
        position: 'absolute',
        top: theme.spacing(2.5),
        right: theme.spacing(3),
        [theme.breakpoints.down('xs')]: {
          right: theme.spacing(1.5)
        }
      },
      addressLines: {
        display: 'flex',
        flexDirection: 'column'
      },
      emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(0.75),
        minHeight: 52,
        padding: theme.spacing(1.5),
        border: `1px dashed ${neutral.P85}`,
        borderRadius: theme.borderRadius.lg,
        color: neutral.P70,
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 400,
        transition: 'border-color 150ms ease, color 150ms ease',
        '@media print': {
          display: 'none'
        }
      },
      emptyStateIcon: {
        fontSize: 18
      },

      // --- mobile header ---
      // On phones the company block is dropped from the card: the doc label / ID / date sit on one
      // summary row above the compact contact rows, and the company detail moves into the document
      // sheet the summary row opens.
      summaryRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 2.5),
        // Slightly washed, so the summary reads as the card's header band above the detail rows.
        backgroundColor: neutral.P98
      },
      summaryLeft: {
        display: 'flex',
        alignItems: 'baseline',
        gap: theme.spacing(1.5),
        minWidth: 0
      },
      summaryRight: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        flexShrink: 0
      },
      summaryLabel: {
        fontSize: theme.typography.pxToRem(13),
        color: neutral.P30
      },
      summaryMuted: {
        fontSize: theme.typography.pxToRem(13),
        color: neutral.P60,
        whiteSpace: 'nowrap'
      },
      summaryRowClickable: {
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: neutral.P96
        }
      },
      // One compact contact row: icon, muted label, then the value. Each row opens the same editor its
      // full-header column does.
      compactRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 2),
        borderTop: border,
        minWidth: 0
      },
      compactRowClickable: {
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: neutral.P98
        }
      },
      compactLabel: {
        color: neutral.P60,
        fontSize: theme.typography.pxToRem(13),
        flexShrink: 0
      },
      compactValue: {
        fontWeight: 600,
        fontSize: theme.typography.pxToRem(13),
        color: neutral.P40,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0
      },
      // An unset value reads as the prompt to add one (the row is the button), so it stays muted rather
      // than bold.
      compactEmptyValue: {
        fontWeight: 400,
        color: theme.palette.primary.main
      },
      compactIcon: {
        fontSize: 18,
        color: neutral.P50,
        flexShrink: 0
      },

      // --- mobile section sheet (QuoteDocumentHeaderMobileSheet) ---
      // Tapping a compact row opens a bottom sheet with that section's full detail, rather than
      // jumping straight into its editor: on a phone the row can only show one truncated value, so
      // the sheet is where the email/phone/address actually become readable and tappable.
      sheetPaper: {
        borderTopLeftRadius: theme.borderRadius.lg,
        borderTopRightRadius: theme.borderRadius.lg,
        // Never taller than the viewport, and clear of the iOS home indicator.
        maxHeight: '80vh',
        '@supports (max-height: 80dvh)': {
          maxHeight: '80dvh'
        },
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      },
      sheetHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1, 1, 1, 2),
        borderBottom: border,
        flexShrink: 0
      },
      sheetTitle: {
        display: 'flex',
        alignItems: 'baseline',
        gap: theme.spacing(1.25),
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 500,
        color: neutral.P30,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      // The document ID beside the label reads as the quieter half of the title.
      sheetTitleId: {
        fontWeight: 400
      },
      sheetCloseButton: {
        padding: theme.spacing(0.5),
        color: neutral.P50
      },
      // One padded tray holding the detail rows at the top and the toolbar at the bottom — no divider
      // between them. The minimum height keeps every section's sheet the same size, so opening one row
      // after another doesn't resize the sheet under the reader's thumb.
      sheetTray: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        minHeight: 275,
        padding: theme.spacing(2.5, 2.5, 4.5),
        overflowY: 'auto'
      },
      sheetRows: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        width: '100%',
        minWidth: 0
      },
      // The icon pins to the first line, because a long value wraps rather than truncating.
      sheetRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        maxWidth: '100%',
        minWidth: 0
      },
      sheetRowIcon: {
        fontSize: 16,
        color: neutral.P50,
        flexShrink: 0,
        // Centres the icon against the value's first line, now that the row aligns to the top.
        marginTop: 8
      },
      // Each value sits in its own outlined pill. A value too long for the sheet wraps inside the pill —
      // the pill grows taller and the corners stay round, rather than the text running off the edge.
      sheetPill: {
        display: 'flex',
        alignItems: 'center',
        minHeight: 32,
        padding: theme.spacing(0.5, 1.5),
        boxSizing: 'border-box',
        minWidth: 0,
        backgroundColor: neutral.White,
        border: `1px solid ${neutral.P85}`,
        borderRadius: 16,
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P50,
        overflowWrap: 'anywhere'
      },
      sheetPillClickable: {
        cursor: 'pointer',
        transition: 'background-color 120ms ease',
        '&:hover': {
          backgroundColor: neutral.P98
        },
        '&:active': {
          backgroundColor: neutral.P96
        }
      },
      sheetToolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexShrink: 0
      },
      // The one action affordance: a 32px round outlined button that opens this section's Edit menu.
      sheetEditButton: {
        width: 32,
        height: 32,
        padding: 0,
        border: `1px solid ${neutral.P85}`,
        backgroundColor: neutral.P98,
        color: neutral.P50,
        '&:hover': {
          backgroundColor: neutral.P96
        }
      },
      // --- document sheet ---
      // The document sheet is read-only company detail, so it drops the pills the editable sections use
      // and renders plain icon + text lines, the way the mock draws it.
      sheetPlainRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(0.75),
        minWidth: 0,
        maxWidth: '100%'
      },
      // Font Awesome fills its em box, where the Material icon this replaced drew its glyph inset in a
      // 24-unit viewBox (~80% ink). Sizes are scaled by 0.8 so the icons read at their old size (ENG-1366).
      sheetPlainIcon: {
        fontSize: 13,
        color: neutral.P50,
        flexShrink: 0,
        // Centres the icon against the value's first line, now that the row aligns to the top.
        marginTop: 2
      },
      sheetPlainText: {
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P50,
        minWidth: 0,
        overflowWrap: 'anywhere'
      },
      sheetCompanyRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        minWidth: 0
      },
      sheetLogo: {
        width: 72,
        height: 72,
        objectFit: 'contain',
        flexShrink: 0
      },
      sheetCompanyName: {
        fontSize: theme.typography.pxToRem(15),
        fontWeight: 500,
        color: neutral.P30,
        minWidth: 0,
        overflowWrap: 'anywhere'
      },
      // The date shares the sheet's bottom row with the toolbar, rather than sitting with the company
      // lines above — it belongs to the document, not to the company.
      sheetDocFooter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        flexShrink: 0
      },

      // A thin rule between menu groups (matches the classic ContactCard menu): a disabled, zero-height
      // MenuItem with a bottom border.
      menuItemDivider: {
        padding: 0,
        height: 0,
        minHeight: 0,
        paddingBottom: theme.spacing(),
        marginBottom: theme.spacing(),
        borderBottom: border
      }
    }
  },
  { name: 'QuoteDocumentHeader' }
)

export default useQuoteDocumentHeaderStyles
